// src/components/editor/EditorStatusBar.jsx
//
// Word-style status strip pinned below the page viewer. It collects the
// controls that used to be scattered — save state and word count in the
// header, zoom floating over the canvas — into one bar, per the manuscript
// editor prototype.
//
// Placement matters: this renders as a flex sibling AFTER .editor-main-content
// and deliberately OUTSIDE it. That container's `padding: 24px 0 56px` is
// mirrored by CONTENT_VPAD in EditorPage and CONTAINER_PAD_TOP/BOTTOM_PADDING
// in EditorRuler ("must match EditorPage.css exactly"), so anything that
// changes its box silently moves the rulers and the fit-page zoom with it.
//
// Page navigation counts rows rather than pages: one page per row in single
// view, two in a spread. EditorPage derives the current page from its scroll
// offset and hands down the two scroll handlers, so this component only has to
// decide how to label the row and when to grey out an end stop.
import React from 'react';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SaveStatusIndicator, { SAVE_STATUS_CONFIG } from '../common/SaveStatusIndicator';
import ZoomControl from './ZoomControl';

// MUI suppresses a Tooltip on a disabled control unless it wraps a real
// element, so the disabled buttons get a plain span to hang off.
const HintedButton = ({ title, disabled, onClick, children }) => (
  <Tooltip title={title}>
    <span style={{ display: 'inline-flex' }}>
      <IconButton size="small" className="esb-btn" disabled={disabled} onClick={onClick}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

export default function EditorStatusBar({
  saveStatus,
  onRetrySave,
  wordCount = 0,
  currentPage = 1,
  pageCount = 1,
  onPrevPage,
  onNextPage,
  viewMode = 'single',
  onViewModeChange,
  zoomMode,
  zoom,
  onSetZoomMode,
  onSetZoom,
  // Set while the Goal tab is open and the manuscript is showing in its middle:
  // the way back to the goals page.
  onBackToGoals,
}) {
  // A spread shows two pages at once, so the indicator names the pair and the
  // end stop accounts for both — otherwise Next stays lit on the last spread of
  // an even-length document with nowhere left to go.
  const perRow = viewMode === 'spread' ? 2 : 1;
  const lastShown = Math.min(currentPage + perRow - 1, pageCount);
  const pageLabel = lastShown > currentPage
    ? `Pages ${currentPage}\u2013${lastShown} of ${pageCount}`
    : `Page ${currentPage} of ${pageCount}`;

  return (
    <Box className="editor-status-bar">
      <Box className="esb-group">
        <HintedButton title="Previous page" disabled={!onPrevPage || currentPage <= 1} onClick={onPrevPage}>
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </HintedButton>
        <span className="esb-pages">{pageLabel}</span>
        <HintedButton
          title="Next page"
          disabled={!onNextPage || currentPage + perRow > pageCount}
          onClick={onNextPage}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </HintedButton>
      </Box>

      <Box className="esb-group">
        {/* Wrapped so the bar's CSS can drop the label to a bare dot when the
            sidebar narrows the editor; the tooltip keeps the words reachable.
            null = no save state to report (the draft never loaded). */}
        {saveStatus != null && (
          <Tooltip title={(SAVE_STATUS_CONFIG[saveStatus] || SAVE_STATUS_CONFIG.saved).text}>
            <Box className={`esb-save${saveStatus === 'error' ? ' is-error' : ''}`}>
              <SaveStatusIndicator status={saveStatus} onRetry={onRetrySave} />
            </Box>
          </Tooltip>
        )}

        {onBackToGoals && (
          <button type="button" className="goals-back-btn" onClick={onBackToGoals}>
            ← Back to goals
          </button>
        )}

        <span className="esb-words">
          {wordCount.toLocaleString()} {wordCount === 1 ? 'word' : 'words'}
        </span>

        <Divider orientation="vertical" flexItem className="esb-divider" />

        <Tooltip title="Single page">
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              size="small"
              className={`esb-btn${viewMode === 'single' ? ' is-active' : ''}`}
              disabled={!onViewModeChange}
              onClick={() => onViewModeChange?.('single')}
            >
              <DescriptionOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Two-page spread">
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              size="small"
              className={`esb-btn${viewMode === 'spread' ? ' is-active' : ''}`}
              disabled={!onViewModeChange}
              onClick={() => onViewModeChange?.('spread')}
            >
              <MenuBookOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem className="esb-divider" />

        <ZoomControl
          variant="inline"
          zoomMode={zoomMode}
          zoom={zoom}
          onSetMode={onSetZoomMode}
          onSetZoom={onSetZoom}
        />
      </Box>
    </Box>
  );
}
